// apps/curbpass/src/routes/vehicles/components/VehicleMobileDrawer.tsx
import React, { useState, useEffect, useCallback } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { DeleteVehicleConfirmDialog } from "./DeleteVehicleConfirmDialog";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  VehicleType,
  VehicleTypeValue,
  VehicleCategory,
  VehicleCategoryValue,
} from "@app.automotus.io/components/common";
import { RegisteredVehicle } from "common/graphql";
import { EditBuffer, NewVehicleRow, VehicleRowErrors, VehicleTableRow } from "../vehicles.types";
import { validateVehicleRow } from "../validateVehicleRow";
import { STATE_OPTIONS, VEHICLE_TYPE_OPTIONS, VEHICLE_CATEGORY_OPTIONS, VehicleSelectOption } from "../vehicleOptions";

interface VehicleMobileDrawerProps {
  open: boolean;
  /** null = add new vehicle mode */
  vehicle: VehicleTableRow | null;
  onClose: () => void;
  onSaveEdit: (id: string, data: EditBuffer) => Promise<void>;
  onSubmitNew: (
    data: Pick<NewVehicleRow, "stateCode" | "licensePlate" | "make" | "model" | "type" | "category">,
  ) => Promise<void>;
  onDeleteExisting: (id: string) => Promise<void>;
  existingVehicles: RegisteredVehicle[];
  newRows: NewVehicleRow[];
}

const EMPTY_BUFFER: EditBuffer = { stateCode: "", licensePlate: "", make: "", model: "", type: "" };

export const VehicleMobileDrawer: React.FC<VehicleMobileDrawerProps> = ({
  open,
  vehicle,
  onClose,
  onSaveEdit,
  onSubmitNew,
  onDeleteExisting,
  existingVehicles,
  newRows,
}) => {
  const isNew = vehicle === null;
  const [mode, setMode] = useState<"detail" | "form">("detail");
  const [editBuffer, setEditBuffer] = useState<EditBuffer>(EMPTY_BUFFER);
  const [newCategory, setNewCategory] = useState<NewVehicleRow["category"]>("commercial");
  const [editErrors, setEditErrors] = useState<VehicleRowErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (isNew) {
      setMode("form");
      setEditBuffer(EMPTY_BUFFER);
      setNewCategory("commercial");
      setEditErrors({
        stateCode: "State is required",
        licensePlate: "License plate is required",
        type: "Vehicle type is required",
      });
    } else if (vehicle) {
      setMode("detail");
      setEditBuffer({
        stateCode: vehicle.stateCode,
        licensePlate: vehicle.licensePlate,
        make: vehicle.make ?? "",
        model: vehicle.model ?? "",
        type: (vehicle.type as VehicleTypeValue | "") ?? "",
      });
      setEditErrors({});
    }
    setSaveError(null);
    setIsSaving(false);
    setShowDeleteConfirm(false);
    setIsDeleting(false);
    setDeleteError(null);
  }, [open, vehicle, isNew]);

  const runValidation = useCallback(
    (buffer: EditBuffer): VehicleRowErrors => {
      const fakeRow: NewVehicleRow = {
        id: vehicle?.id ?? "__new__",
        ...buffer,
        category: "commercial",
        errors: {},
      };
      const otherExisting = vehicle ? existingVehicles.filter((v) => v.id !== vehicle.id) : existingVehicles;
      return validateVehicleRow(fakeRow, newRows, otherExisting).errors;
    },
    [vehicle, existingVehicles, newRows],
  );

  const handleBufferChange = useCallback(
    (patch: Partial<EditBuffer>) => {
      const updated = { ...editBuffer, ...patch };
      setEditBuffer(updated);
      setEditErrors(runValidation(updated));
    },
    [editBuffer, runValidation],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!vehicle) {
      return;
    }
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDeleteExisting(vehicle.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch {
      setDeleteError("Failed to remove vehicle. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [vehicle, onDeleteExisting, onClose]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      if (isNew) {
        await onSubmitNew({
          stateCode: editBuffer.stateCode,
          licensePlate: editBuffer.licensePlate,
          make: editBuffer.make,
          model: editBuffer.model,
          type: editBuffer.type,
          category: newCategory,
        });
      } else {
        if (!vehicle) {
          return;
        }
        await onSaveEdit(vehicle.id, editBuffer);
      }
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : null;
      setSaveError(msg ?? "Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [isNew, vehicle, editBuffer, newCategory, onSaveEdit, onSubmitNew, onClose]);

  const hasErrors = Object.keys(editErrors).length > 0;
  const selectedState = STATE_OPTIONS.find((o) => o.value === editBuffer.stateCode) ?? null;

  const typeLabel = vehicle?.type
    ? VehicleType.FromValue(vehicle.type as VehicleTypeValue)?.label ?? vehicle.type
    : "—";
  const categoryLabel = vehicle?.category
    ? VehicleCategory.FromValue(vehicle.category as VehicleCategoryValue)?.label ?? vehicle.category
    : "—";

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: "20px 20px 0 0", maxHeight: "90vh", display: "flex", flexDirection: "column" },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ width: 36, height: 4, bgcolor: "grey.300", borderRadius: 2, mx: "auto", mt: 1.5, flexShrink: 0 }} />

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {/* Header */}
        <Box
          sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {isNew ? "Add Vehicle" : vehicle?.licensePlate || "—"}
            </Typography>
            {!isNew && vehicle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {[
                  vehicle.stateCode,
                  vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : vehicle.make || vehicle.model,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ ml: 1, mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Detail view */}
        {mode === "detail" && vehicle && (
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Vehicle Details
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              {[
                { label: "State", value: vehicle.stateCode || "—" },
                { label: "Plate Number", value: vehicle.licensePlate || "—" },
                { label: "Make", value: vehicle.make || "—" },
                { label: "Model", value: vehicle.model || "—" },
                { label: "Type", value: typeLabel },
                { label: "Category", value: categoryLabel },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Form view */}
        {mode === "form" && (
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {isNew ? "Vehicle Details" : "Edit Vehicle"}
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
              <Autocomplete
                size="small"
                options={STATE_OPTIONS}
                value={selectedState}
                onChange={(_e, option) => handleBufferChange({ stateCode: option?.value ?? "" })}
                getOptionLabel={(o: VehicleSelectOption) => o.value}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                )}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    error={!!editErrors.stateCode}
                    helperText={editErrors.stateCode}
                  />
                )}
              />
              <TextField
                size="small"
                label="Plate Number"
                value={editBuffer.licensePlate}
                onChange={(e) => handleBufferChange({ licensePlate: e.target.value.replace(/\s/g, "").toUpperCase() })}
                error={!!editErrors.licensePlate}
                helperText={editErrors.licensePlate}
                inputProps={{ autoCapitalize: "characters" }}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
              <TextField
                size="small"
                label="Make"
                value={editBuffer.make}
                onChange={(e) => handleBufferChange({ make: e.target.value })}
                placeholder="Toyota"
              />
              <TextField
                size="small"
                label="Model"
                value={editBuffer.model}
                onChange={(e) => handleBufferChange({ model: e.target.value })}
                placeholder="Camry"
              />
            </Box>

            <FormControl size="small" fullWidth error={!!editErrors.type} sx={{ mb: 1.5 }}>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={editBuffer.type}
                onChange={(e) => handleBufferChange({ type: e.target.value as VehicleTypeValue | "" })}
              >
                {VEHICLE_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {editErrors.type && <FormHelperText>{editErrors.type}</FormHelperText>}
            </FormControl>

            {isNew && (
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as NewVehicleRow["category"])}
                >
                  {VEHICLE_CATEGORY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {saveError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                {saveError}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ flexShrink: 0 }}>
        <Divider />
        <Stack direction="row" spacing={1.5} sx={{ px: 2.5, py: 2 }}>
          {mode === "detail" && (
            <>
              <Button variant="contained" fullWidth startIcon={<EditIcon />} onClick={() => setMode("form")}>
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                sx={{ minWidth: 48, px: 1.5 }}
                onClick={() => setShowDeleteConfirm(true)}
              >
                <DeleteIcon />
              </Button>
            </>
          )}
          {mode === "form" && (
            <>
              {!isNew && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setMode("detail");
                    setSaveError(null);
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              )}
              <Button variant="contained" fullWidth onClick={handleSave} disabled={hasErrors || isSaving}>
                {isSaving ? <CircularProgress size={20} color="inherit" /> : isNew ? "Add Vehicle" : "Save"}
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Delete confirmation dialog */}
      <DeleteVehicleConfirmDialog
        open={showDeleteConfirm}
        isDeleting={isDeleting}
        errorMessage={deleteError}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteError(null);
        }}
      />
    </Drawer>
  );
};

export default VehicleMobileDrawer;
